import {
  InterviewDecision,
  type InterviewLoaded,
} from "@repo/app-types/entities/interview";
import { MessageTemplateType } from "@repo/app-types/entities/messageTemplate";
import { isRecruitingModeProject } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { renderSingleLineContent } from "@repo/ui/content/renderers";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { useAllMessageTemplatesQuery } from "../../../api/entities/messageTemplate.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import { AssessmentFeedbackDecisionModal } from "../../insights/assessmentFeedback/decisionModal.js";
import type { AssessmentFeedbackFormValues } from "../../insights/assessmentFeedback/form.js";
import {
  checkIsInconsistentDecision,
  trackAssessmentFeedback,
  type InterviewDecisionWithScore,
} from "../../insights/assessmentFeedback/helpers.js";
import { selectProjectById } from "../../projects/projectsSlice.js";
import { updateInterview } from "../interviewsSlice.js";

interface InterviewDecisionButtonsProps {
  interview: InterviewLoaded;
}

export const InterviewDecisionButtons: FC<InterviewDecisionButtonsProps> = ({
  interview,
}) => {
  const dispatch = useAppDispatch();
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [decisionPendingFeedback, setDecisionPendingFeedback] = useState<
    InterviewDecisionWithScore | undefined
  >(undefined);

  const project = useAppSelector((state) =>
    selectProjectById(state, interview.project_id),
  );
  const { data: allMessageTemplates } = useAllMessageTemplatesQuery();

  if (!project || !isRecruitingModeProject(project)) {
    return null;
  }

  const acceptanceTemplate = allMessageTemplates?.find(
    (t) =>
      t.id === project.settings.acceptance_template_id &&
      t.type === MessageTemplateType.Advanced,
  );

  const rejectionTemplate = allMessageTemplates?.find(
    (t) =>
      t.id === project.settings.rejection_template_id &&
      t.type === MessageTemplateType.Rejected,
  );

  const onDecisionChange = (decision: InterviewDecision | null) => {
    dispatch(
      updateInterview({
        projectId: interview.project_id,
        interviewId: interview.id,
        update: {
          decision,
        },
      }),
    );

    if (decision === null) {
      setShowUndoDialog(false);
    }

    // Try to collect feedback if the decision is inconsistent with the score.
    const originalScore = interview.insights?.assessment_score ?? null;
    // Don't collect feedback if there's already negative feedback.
    const hasNegativeAssessmentFeedback =
      interview.metadata?.assessment_feedback?.sentiment === "negative";
    if (
      decision !== null &&
      originalScore !== null &&
      !hasNegativeAssessmentFeedback &&
      checkIsInconsistentDecision({ originalScore, decision })
    ) {
      setDecisionPendingFeedback({ originalScore, decision });
    }
  };

  const handleFeedbackSubmit = (
    { originalScore, decision }: InterviewDecisionWithScore,
    { suggestedScore, comments }: AssessmentFeedbackFormValues,
  ) => {
    trackAssessmentFeedback({
      projectId: interview.project_id,
      interviewId: interview.id,
      originalScore,
      suggestedScore,
      comments,
      decision,
    });

    if (suggestedScore !== undefined) {
      dispatch(
        updateInterview({
          projectId: interview.project_id,
          interviewId: interview.id,
          update: {
            metadata: {
              assessment_feedback: {
                // Set the sentiment so it reflects on the thumbs up/down widget. Assume a negative sentiment due to decision being inconsistent with the score.
                sentiment: "negative",
                suggested_score: suggestedScore,
                comments,
              },
            },
          },
        }),
      );
    }

    showToast({
      title: "Thank you for your feedback!",
      variant: ToastVariant.Success,
    });
  };

  const renderAdvanceButton = () => (
    <Button
      icon={
        <IconCircleCheck aria-hidden="true" className="h-4 w-4 text-success" />
      }
      onClick={() => {
        onDecisionChange(InterviewDecision.Advanced);
      }}
      size={Size.MEDIUM}
      variant={ButtonVariant.Secondary}
    >
      Advance
    </Button>
  );

  const renderRejectButton = () => (
    <Button
      icon={
        <IconCircleX aria-hidden="true" className="h-4 w-4 text-destructive" />
      }
      onClick={() => {
        onDecisionChange(InterviewDecision.Rejected);
      }}
      size={Size.MEDIUM}
      variant={ButtonVariant.Secondary}
    >
      Reject
    </Button>
  );

  if (interview.decision === InterviewDecision.Advanced) {
    return (
      <>
        <Button
          icon={
            <IconCircleCheck
              aria-hidden="true"
              className="h-4 w-4 text-success"
            />
          }
          onClick={() => {
            setShowUndoDialog(true);
          }}
          size={Size.MEDIUM}
          variant={ButtonVariant.Secondary}
        >
          Advanced
        </Button>
        <SimpleModal
          confirmButtonText="Undo"
          isOpen={showUndoDialog}
          onCancel={() => {
            setShowUndoDialog(false);
          }}
          onConfirm={() => {
            onDecisionChange(null);
          }}
          subtitle="Are you sure you want to undo advancing this candidate?"
          title="Undo advancement"
          variant={SimpleModalVariant.Standard}
        />
        <AssessmentFeedbackDecisionModal
          decision={decisionPendingFeedback}
          onRequestClose={() => setDecisionPendingFeedback(undefined)}
          onSubmit={handleFeedbackSubmit}
        />
      </>
    );
  }

  if (interview.decision === InterviewDecision.Rejected) {
    return (
      <>
        <Button
          icon={
            <IconCircleX
              aria-hidden="true"
              className="h-4 w-4 text-destructive"
            />
          }
          onClick={() => {
            setShowUndoDialog(true);
          }}
          size={Size.MEDIUM}
          variant={ButtonVariant.Secondary}
        >
          Rejected
        </Button>
        <SimpleModal
          confirmButtonText="Undo"
          isOpen={showUndoDialog}
          onCancel={() => {
            setShowUndoDialog(false);
          }}
          onConfirm={() => {
            onDecisionChange(null);
          }}
          subtitle="Are you sure you want to undo rejecting this candidate?"
          title="Undo rejection"
          variant={SimpleModalVariant.Standard}
        />
        <AssessmentFeedbackDecisionModal
          decision={decisionPendingFeedback}
          onRequestClose={() => setDecisionPendingFeedback(undefined)}
          onSubmit={handleFeedbackSubmit}
        />
      </>
    );
  }

  return (
    <>
      {acceptanceTemplate ? (
        <TooltipTrigger>
          {renderAdvanceButton()}
          <SimpleTooltip>
            Advance the candidate and send the &quot;
            {renderSingleLineContent(acceptanceTemplate.subject)}&quot;
            advancement message.
          </SimpleTooltip>
        </TooltipTrigger>
      ) : (
        renderAdvanceButton()
      )}

      {rejectionTemplate ? (
        <TooltipTrigger>
          {renderRejectButton()}
          <SimpleTooltip>
            Reject the candidate and send the &quot;
            {renderSingleLineContent(rejectionTemplate.subject)}&quot; rejection
            message.
          </SimpleTooltip>
        </TooltipTrigger>
      ) : (
        renderRejectButton()
      )}
    </>
  );
};
